import { useState } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { useDeleteNotificationByIdMutation, useUpdateNotificationByIdMutation } from './notificationsApi';

/**
 * Shared edit-mode state and bulk action handlers for any screen that renders a list of notification cards
 * (currently the notifications list and the archive). Pair with `NotificationsSelectAllCheckbox` and
 * `NotificationsBulkActionBar` to get the same bulk action UI on each screen.
 *
 * @param allVisibleNotificationIds - The IDs of every notification currently rendered, used for "Select All"
 */
export function useNotificationsBulkEdit(allVisibleNotificationIds: string[]) {
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true, position: 'top' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAllCheckboxes, setShowAllCheckboxes] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteNotification] = useDeleteNotificationByIdMutation();
  const [updateNotification] = useUpdateNotificationByIdMutation();
  const deleteConfirm = useDisclosure();
  const archiveConfirm = useDisclosure();

  function handleEnterEditMode() {
    setIsEditMode(true);
    setShowAllCheckboxes(true);
  }

  function handleExitEditMode() {
    setIsEditMode(false);
    setShowAllCheckboxes(false);
    setSelectedIds(new Set());
  }

  function handleSelectAll() {
    if (selectedIds.size === allVisibleNotificationIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleNotificationIds));
      setShowAllCheckboxes(true);
    }
  }

  function handleSwipeSelect(notificationId: string) {
    if (!isEditMode) {
      setIsEditMode(true);
      // Swipe-select does NOT show all checkboxes — only the swiped card reveals its own
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(notificationId);
      return next;
    });
  }

  // Auto-exit edit mode when all are deselected via individual toggles
  function handleToggleSelectWithAutoExit(notificationId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(notificationId)) {
        next.delete(notificationId);
      } else {
        next.add(notificationId);
      }
      if (next.size === 0) {
        setIsEditMode(false);
        setShowAllCheckboxes(false);
      }
      return next;
    });
  }

  function handleDeletePressed() {
    if (selectedIds.size > 1) {
      deleteConfirm.onOpen();
    } else {
      handleBulkDelete();
    }
  }

  function handleArchivePressed() {
    if (selectedIds.size > 1) {
      archiveConfirm.onOpen();
    } else {
      handleBulkArchive();
    }
  }

  // The bulk handlers capture the selected IDs, then exit edit mode synchronously so the action bar dismisses on
  // tap (the per-item cache patches in `notificationsApi` remove the cards optimistically). The mutations run in
  // the background; on failure the optimistic patches roll back and an error toast is shown.

  async function handleBulkDelete() {
    deleteConfirm.onClose();
    const ids = Array.from(selectedIds);
    handleExitEditMode();
    try {
      await Promise.all(ids.map((id) => deleteNotification(id).unwrap()));
      toast({ title: t('Notifications.notifications deleted'), status: 'success' });
    } catch {
      toast({
        title: t('Notifications.error deleting notification'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }

  async function handleBulkArchive() {
    archiveConfirm.onClose();
    const ids = Array.from(selectedIds);
    handleExitEditMode();
    try {
      await Promise.all(ids.map((id) => updateNotification({ notificationId: id, body: { archived: true } }).unwrap()));
      toast({ title: t('Notifications.notifications archived'), status: 'success' });
    } catch {
      toast({
        title: t('Notifications.error archiving notification'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }

  async function handleBulkMarkAsRead() {
    const ids = Array.from(selectedIds);
    handleExitEditMode();
    try {
      await Promise.all(ids.map((id) => updateNotification({ notificationId: id, body: { read: true } }).unwrap()));
      toast({ title: t('Notifications.notifications marked as read'), status: 'success' });
    } catch {
      toast({
        title: t('Notifications.error marking notification as read'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }

  async function handleBulkMarkAsUnread() {
    const ids = Array.from(selectedIds);
    handleExitEditMode();
    try {
      await Promise.all(ids.map((id) => updateNotification({ notificationId: id, body: { read: false } }).unwrap()));
      toast({ title: t('Notifications.notifications marked as unread'), status: 'success' });
    } catch {
      toast({
        title: t('Notifications.error marking notification as unread'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }

  return {
    isEditMode,
    showAllCheckboxes,
    selectedIds,
    allVisibleNotificationIds,
    handleEnterEditMode,
    handleExitEditMode,
    handleSelectAll,
    handleSwipeSelect,
    handleToggleSelectWithAutoExit,
    handleDeletePressed,
    handleArchivePressed,
    handleBulkDelete,
    handleBulkArchive,
    handleBulkMarkAsRead,
    handleBulkMarkAsUnread,
    deleteConfirm,
    archiveConfirm,
  };
}

export type NotificationsBulkEdit = ReturnType<typeof useNotificationsBulkEdit>;
